import { Routes } from '@angular/router';
import { authGuard } from './common/auth/guards/auth.guard';
import { noAuthGuard } from './common/auth/guards/no-auth.guard';
import { teacherGuard } from './common/auth/guards/teacher.guard';
import { studentGuard } from './common/auth/guards/student.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./features/app-landing/landing').then((m) => m.LandingComponent),
    canActivate: [noAuthGuard],
  },
  {
    path: 'assignment-landing',
    loadComponent: () =>
      import('./features/assignment-landing/assignment-landing').then(
        (m) => m.AssignmentLandingComponent
      ),
  },
  {
    path: 'ai-tutor-landing',
    loadComponent: () =>
      import('./features/ai-tutor-landing/ai-tutor-landing').then(
        (m) => m.AiTutorLandingComponent
      ),
  },
  {
    path: 'grading-landing',
    loadComponent: () =>
      import('./features/grading-landing/grading-landing').then(
        (m) => m.GradingLandingComponent
      ),
  },
  {
    path: 'lesson-plan-landing',
    loadComponent: () =>
      import('./features/lesson-plan-landing/lesson-plan-landing').then(
        (m) => m.LessonPlanLandingComponent
      ),
  },
  {
    path: 'ai-tutor/:id',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/teacher/ai-tutor/ai-tutor').then((m) => m.AiTutorComponent),
        pathMatch: 'full',
      },
      {
        path: 'students/:chatId',
        loadComponent: () =>
          import(
            './features/teacher/ai-tutor-students/student-chat-view/student-chat-view'
          ).then((m) => m.StudentChatViewComponent),
        canActivate: [authGuard, teacherGuard],
      },
      {
        path: '',
        loadComponent: () =>
          import('./features/teacher/ai-tutor-detail/ai-tutor-detail').then(
            (m) => m.AiTutorDetailComponent
          ),
        canActivate: [authGuard, teacherGuard],
        children: [
          {
            path: 'view',
            loadComponent: () =>
              import('./features/teacher/ai-tutor-view/ai-tutor-view').then(
                (m) => m.AiTutorViewComponent
              ),
          },
          {
            path: 'students',
            loadComponent: () =>
              import('./features/teacher/ai-tutor-students/ai-tutor-students').then(
                (m) => m.AiTutorStudentsComponent
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'auth-successful',
    loadComponent: () =>
      import('./common/auth/auth-successful/auth-successful').then(
        (m) => m.AuthSuccessfulComponent
      ),
  },
  {
    path: 'login',
    loadComponent: () => import('./common/auth/login/login').then((m) => m.LoginComponent),
    canActivate: [noAuthGuard],
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./common/auth/sign-up/sign-up').then((m) => m.SignUpComponent),
  },

  {
    path: 'teacher/dashboard',
    loadComponent: () =>
      import('./features/teacher/dashboard/dashboard').then((m) => m.DashboardComponent),
    canActivate: [authGuard, teacherGuard],
    loadChildren: () =>
      import('./features/teacher/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
  },
  {
    path: 'lesson-plan',
    loadComponent: () =>
      import('./features/teacher/lesson-plan/lesson-plan').then((m) => m.LessonPlanComponent),
    loadChildren: () =>
      import(
        './features/teacher/lesson-plan/steps/step-content/step-content.routes'
      ).then((m) => m.stepContentRoutes),
  },
  {
    path: 'lesson-plan/:id',
    loadComponent: () =>
      import('./features/teacher/lesson-plan/lesson-plan').then((m) => m.LessonPlanComponent),
    loadChildren: () =>
      import(
        './features/teacher/lesson-plan/steps/step-content/step-content.routes'
      ).then((m) => m.stepContentRoutes),
  },
  {
    path: 'grading',
    loadComponent: () =>
      import('./features/teacher/grading-chat/grading-chat.component').then(
        (m) => m.GradingChatComponent
      ),
  },
  {
    path: 'grading/:id',
    loadComponent: () =>
      import('./features/teacher/grading-chat/grading-chat.component').then(
        (m) => m.GradingChatComponent
      ),
  },
  {
    path: 'assignment',
    loadComponent: () =>
      import('./features/teacher/assignment/assignment').then((m) => m.AssignmentComponent),
  },
  {
    path: 'assignment/:id',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/teacher/assignment/assignment').then((m) => m.AssignmentComponent),
        pathMatch: 'full',
      },
      {
        path: 'submission/:submissionId',
        loadComponent: () =>
          import('./features/teacher/submission-review/submission-review').then(
            (m) => m.SubmissionReviewComponent
          ),
        canActivate: [authGuard, teacherGuard],
      },
      {
        path: '',
        loadComponent: () =>
          import('./features/teacher/assignment-detail/assignment-detail').then(
            (m) => m.AssignmentDetailComponent
          ),
        canActivate: [authGuard, teacherGuard],
        children: [
          {
            path: 'view',
            loadComponent: () =>
              import('./features/teacher/assignment-view/assignment-view').then(
                (m) => m.AssignmentViewComponent
              ),
          },
          {
            path: 'students',
            loadComponent: () =>
              import('./features/teacher/assignment-students/assignment-students').then(
                (m) => m.AssignmentStudentsComponent
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'student/dashboard',
    loadComponent: () =>
      import('./features/student/dashboard/student-dashboard').then(
        (m) => m.StudentDashboardComponent
      ),
    canActivate: [authGuard, studentGuard],
    loadChildren: () =>
      import('./features/student/dashboard/student-dashboard.routes').then(
        (m) => m.studentDashboardRoutes
      ),
  },
  {
    path: 'join-activity',
    loadComponent: () =>
      import('./features/join-activity/join-activity').then((m) => m.JoinActivityComponent),
  },
  {
    path: 'activity/assignment/:id',
    loadComponent: () =>
      import('./features/student/assignment/student-assignment').then(
        (m) => m.StudentAssignmentComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'activity/ai-tutor/:id',
    loadComponent: () =>
      import('./features/student/ai-tutor/student-ai-tutor').then(
        (m) => m.StudentAiTutorComponent
      ),
    canActivate: [authGuard],
  },
  {
    path: 'a/:shareCode',
    loadComponent: () =>
      import('./features/activity-redirect/activity-redirect').then(
        (m) => m.ActivityRedirectComponent
      ),
  },
  {
    path: 't/:shareCode',
    loadComponent: () =>
      import('./features/activity-redirect/activity-redirect').then(
        (m) => m.ActivityRedirectComponent
      ),
  },
  {
    path: 'subscription-terms',
    loadComponent: () =>
      import('./common/components/legal/subscription-terms/subscription-terms.component').then(
        (m) => m.SubscriptionTermsComponent
      ),
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./common/components/legal/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
  },
  {
    path: 'terms-of-use',
    loadComponent: () =>
      import('./common/components/legal/terms-of-use/terms-of-use.component').then(
        (m) => m.TermsOfUseComponent
      ),
  },
  {
    path: 'email-preferences',
    loadComponent: () =>
      import('./common/components/email-preferences/email-preferences.component').then(
        (m) => m.EmailPreferencesComponent
      ),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./common/auth/role-redirect/role-redirect').then((m) => m.RoleRedirectComponent),
  },
];
